import { Form, Formik } from 'formik';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { ConfirmDialog, useConfirmDialogComponent } from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { Reminder } from 'src/models/reminder/_types';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { Identifier } from 'src/types/utilityTypes';
import * as Yup from 'yup';

const DESCRIPTION_MAX_LENGTH = 500;

const getAutomationLabel = (
  reminder: Reminder,
  fallback: string
) => reminder.name || fallback;

export interface AutomationGroupDialogValues {
  description: string;
  name: string;
  reminderIds: Identifier[];
}

interface CreateAutomationGroupDialogProps {
  initialValues?: Partial<AutomationGroupDialogValues>;
  onBack?: () => void;
  onClose: () => void;
  onDelete?: () => void | Promise<void>;
  onSave: (values: AutomationGroupDialogValues) => Promise<void>;
  open: boolean;
  selectableReminders?: Reminder[];
  variant?: 'create' | 'edit';
}

export const CreateAutomationGroupDialog: FC<CreateAutomationGroupDialogProps> = ({
  initialValues,
  onBack,
  onClose,
  onDelete,
  onSave,
  open,
  selectableReminders = [],
  variant = 'create',
}) => {
  const { t } = useTranslation();
  const isEdit = variant === 'edit';
  const [deleteConfirmProps, confirmDelete] = useConfirmDialogComponent();
  const deleteConfirmOpenRef = useRef(false);

  const formInitialValues: AutomationGroupDialogValues = {
    description: initialValues?.description ?? '',
    name: initialValues?.name ?? '',
    reminderIds: initialValues?.reminderIds ?? [],
  };

  const automationOptions = useMemo(
    () =>
      [...selectableReminders].sort((left, right) =>
        (left.name || '').localeCompare(right.name || '', undefined, { sensitivity: 'base' })
      ),
    [selectableReminders]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        description: Yup.string().max(
          DESCRIPTION_MAX_LENGTH,
          t('automations.groups.descriptionTooLong', { max: DESCRIPTION_MAX_LENGTH })
        ),
        name: Yup.string().trim().required(t('automations.groups.nameRequired')),
        reminderIds: Yup.array()
          .min(1, t('automations.groups.addAutomationsRequired'))
          .required(t('automations.groups.addAutomationsRequired')),
      }),
    [t]
  );

  const title = isEdit
    ? t('automations.groups.editGroup')
    : t('automations.groups.createGroup');

  const handleDeleteClick = async () => {
    if (!onDelete) {
      return;
    }

    deleteConfirmOpenRef.current = true;
    try {
      const confirmed = await confirmDelete({
        message: t('automations.groups.deleteConfirm'),
        title: t('automations.groups.deleteTitle'),
      });
      if (confirmed) {
        await onDelete();
      }
    } finally {
      window.setTimeout(() => {
        deleteConfirmOpenRef.current = false;
      }, 0);
    }
  };

  const handleDialogClose = () => {
    if (deleteConfirmOpenRef.current) {
      return;
    }
    onClose();
  };

  return (
    <>
    <Formik<AutomationGroupDialogValues>
      enableReinitialize={!isEdit}
      initialValues={formInitialValues}
      isInitialValid={false}
      validateOnMount={false}
      validationSchema={validationSchema}
      onSubmit={async (values, helpers) => {
        await onSave({
          description: values.description.trim(),
          name: values.name.trim(),
          reminderIds: values.reminderIds,
        });
        helpers.resetForm();
        handleDialogClose();
      }}
    >
      {({ errors, isSubmitting, setFieldTouched, setFieldValue, submitForm, touched, values }) => {
        const selectedReminders = automationOptions.filter((reminder) =>
          values.reminderIds.some((id) => String(id) === String(reminder.id))
        );
        const reminderIdsError = touched.reminderIds ? errors.reminderIds : undefined;
        const canSubmit =
          values.name.trim().length > 0 &&
          values.reminderIds.length > 0 &&
          !errors.name &&
          !errors.description &&
          !errors.reminderIds;

        const dialogActions = (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              width: '100%',
            }}
          >
            {isEdit && onDelete ? (
              <Button color="error" onClick={handleDeleteClick} variant="bordered">
                {t('automations.groups.deleteGroup')}
              </Button>
            ) : onBack ? (
              <Button onClick={onBack} variant="borderless">
                {t('common.back')}
              </Button>
            ) : null}
            <Box flex={1} />
            <Button onClick={handleDialogClose} variant="bordered">
              {t('common.buttons.cancel')}
            </Button>
            <Button
              disabled={!canSubmit}
              loading={isSubmitting}
              onClick={submitForm}
              variant="prominent"
            >
              {isEdit ? t('common.buttons.save') : t('automations.groups.createGroup')}
            </Button>
          </Box>
        );

        return (
          <Dialog
            actions={dialogActions}
            fullWidth
            maxWidth="sm"
            onClose={handleDialogClose}
            open={open}
            title={title}
          >
            <Form noValidate>
              <Stack spacing={2}>
                <FormTextField
                  autoFocus
                  fullWidth
                  label={t('automations.groups.groupName')}
                  name="name"
                  required
                />

                <Box>
                  <Autocomplete<Reminder, true>
                    disableCloseOnSelect
                    getOptionLabel={(option) =>
                      getAutomationLabel(option, t('automations.common.automation'))
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    multiple
                    onChange={(_, newValue) => {
                      void setFieldValue(
                        'reminderIds',
                        newValue.map((reminder) => reminder.id),
                        true
                      );
                      void setFieldTouched('reminderIds', true, false);
                    }}
                    onClose={() => {
                      void setFieldTouched('reminderIds', true, false);
                    }}
                    options={automationOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={Boolean(reminderIdsError)}
                        label={t('automations.groups.selectAutomations')}
                        placeholder={t('common.actions.search')}
                        required
                      />
                    )}
                    renderOption={(props, option, { selected }) => {
                      const { key, ...optionProps } = props;
                      const label = getAutomationLabel(
                        option,
                        t('automations.common.automation')
                      );

                      return (
                        <Box
                          component="li"
                          key={key ?? String(option.id)}
                          {...optionProps}
                          sx={[
                            {
                              alignItems: 'center',
                              display: 'flex',
                              gap: 1,
                              px: 2,
                              py: 1,
                            },
                            selected
                              ? {
                                  bgcolor: 'primary.lightest',
                                  fontWeight: 600,
                                }
                              : {},
                          ]}
                        >
                          <Checkbox
                            checked={selected}
                            disableRipple
                            size="small"
                            sx={{ py: 0 }}
                            tabIndex={-1}
                          />
                          <Typography variant="body2">{label}</Typography>
                        </Box>
                      );
                    }}
                    value={selectedReminders}
                  />
                  <Typography
                    color={reminderIdsError ? 'error' : 'text.secondary'}
                    sx={{ display: 'block', mt: 0.5 }}
                    variant="caption"
                  >
                    {reminderIdsError
                      ? String(reminderIdsError)
                      : t('automations.groups.addAutomationsNote')}
                  </Typography>
                </Box>

                <FormTextField
                  fullWidth
                  helperText={t('automations.groups.descriptionCount', {
                    count: values.description.length,
                    max: DESCRIPTION_MAX_LENGTH,
                  })}
                  slotProps={{ htmlInput: { maxLength: DESCRIPTION_MAX_LENGTH } }}
                  label={t('automations.groups.description')}
                  minRows={3}
                  multiline
                  name="description"
                />
              </Stack>
            </Form>
          </Dialog>
        );
      }}
    </Formik>
    <ConfirmDialog {...deleteConfirmProps} />
    </>
  );
};
